import { Classes, Dialog } from "@blueprintjs/core"
import { observer } from "mobx-react"
import { formatMoney } from "modules/common/components/money/Money"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { isNewlyCreated } from "modules/common/models/entity"
import { MaterialValuesStoreType } from "modules/dictionaries/inventory/models/material-value-store"
import { OwnMaterialsWidget } from "modules/main/components/own-materials/OwnMaterialsWidget"
import { OwnPremiumsWidget } from "modules/main/components/own-premiums/OwnPremiumsWidget"
import { RoleDetails } from "modules/session/access/components/roles/RoleDetails"
import { useMst } from "modules/root/models/hooks"
import { can } from "modules/session/auth/access"
import sessionFunc from "modules/session/functionalities"
import spendingFunc from "modules/spending/functionalities"
import moment from "moment"
import React, { useState } from "react"
import { EmployeeStoreType } from "../models/employee-store"
import styles from "./EmployeePage.module.scss"
import { CompensationDialog } from "./CompensationDialog"
import { RemoveConfirmation } from "modules/common/components/form/RemoveConfirmation"

type Panel = "positions" | "compensation" | "premiums" | "materials" | "access" | null

interface EmployerCardTitleProps {
  header: string
  store: EmployeeStoreType
  employerId: string
  access: string[]
  materials: MaterialValuesStoreType
  orderAccess: boolean
}

/**
 * Заголовок карточки сотрудника с кнопками «Должности», «Назначенные премии», «Закрепленный
 * инвентарь», «Права доступа» и их окнами. Общий для страницы «Сотрудники» и дерева структуры.
 */
export const EmployerCardTitle = observer(
  ({ header, store, employerId, access, materials, orderAccess }: EmployerCardTitleProps) => {
    const [panel, setPanel] = useState<Panel>(null)
    const close = () => setPanel(null)
    const { session } = useMst()
    // администратора назначает только администратор и не самому себе (проверяет и сервер)
    const adminReason = !session.godMode
      ? "Назначать администраторов может только администратор"
      : session.userId === store.details.id
        ? "Нельзя изменить роль администратора у самого себя"
        : undefined
    const showButtons = !isNewlyCreated(employerId)
    const canAccess = showButtons && can(sessionFunc.ACCESS_EDIT, access)
    // оклады видны только тем, кому видна штатка
    const canCompensation = showButtons && can(spendingFunc.SCHEDULE_READ, access)
    const canEditCompensation = can(spendingFunc.SCHEDULE_EDIT, access)
    // отменить можно последний перевод, пока он не вступил в прошлые месяцы
    const lastTransfer = store.details.employerPositions.length > 1 ? store.details.employerPositions[0] : null
    const portfolioName = (id: string | null) =>
      (id && store.portfolios.portfolios.find((p) => p.id === id)?.label) || ""
    const canRevertTransfer =
      !!lastTransfer &&
      can(spendingFunc.EMPLOYER_EDIT, access) &&
      !parseSince(lastTransfer.since).isBefore(moment().startOf("month"), "month")

    return (
      <div className={styles.title}>
        {header}
        {showButtons && store.details.employerPositions.length > 1 && (
          <PlanrButton type="secondary" size="small" onClick={() => setPanel("positions")}>
            Должности
          </PlanrButton>
        )}
        {canCompensation && (
          <PlanrButton type="secondary" size="small" onClick={() => setPanel("compensation")}>
            Условия оплаты
          </PlanrButton>
        )}
        {showButtons && (
          <PlanrButton type="secondary" size="small" onClick={() => setPanel("premiums")}>
            Назначенные премии
          </PlanrButton>
        )}
        {showButtons && (
          <PlanrButton type="secondary" size="small" onClick={() => setPanel("materials")}>
            Закрепленный инвентарь
          </PlanrButton>
        )}
        {canAccess && (
          <PlanrButton type="secondary" size="small" onClick={() => setPanel("access")}>
            Права доступа
          </PlanrButton>
        )}

        <Dialog
          icon="info-sign"
          title="Должности"
          canEscapeKeyClose={true}
          canOutsideClickClose={false}
          isOpen={panel === "positions"}
          onClose={close}
          style={{ width: `1400px` }}
          backdropClassName="standard"
        >
          <div className={`${Classes.DIALOG_BODY}`}>
            {canRevertTransfer && lastTransfer && (
              <div className={styles.revertTransfer}>
                <RemoveConfirmation
                  onConfirmed={async () => {
                    if (await store.details.revertTransfer()) {
                      close()
                    }
                  }}
                  what={() =>
                    `перевод на должность «${lastTransfer.name}» с ${lastTransfer.since} — вернутся прежние должность, ранг, организация и условия оплаты`
                  }
                  actionName="отменить"
                  render={({ confirmRemoving }) => (
                    <PlanrButton type="lightdanger" size="small" onClick={confirmRemoving}>
                      Отменить перевод
                    </PlanrButton>
                  )}
                />
              </div>
            )}
            <div className={styles.positions}>
              <div className={styles.rowPosition} style={{ display: "flex", fontWeight: "bold" }}>
                <div className={styles.positionName}>Должность</div>
                <div className={styles.positionOrg}>Организация</div>
                <div className={styles.positionName}>Отдел</div>
                <div className={styles.positionName}>Ранг</div>
                <div className={styles.positionMoney}>Дата с</div>
                <div className={styles.positionMoney}>Оклад</div>
                <div className={styles.positionMoney}>Премия</div>
              </div>
              {buildPositionRankHistory(store.details.employerPositions, store.details.gradeHistory, portfolioName).map((row, index) => (
                <div key={index} className={styles.rowPosition} style={{ display: "flex" }}>
                  <div className={styles.positionName}>{row.name}</div>
                  <div className={styles.positionOrg}>
                    {row.organization}
                    {row.organizationNote && <div className={styles.positionNote}>{row.organizationNote}</div>}
                  </div>
                  <div className={styles.positionName}>{row.department}</div>
                  <div className={styles.positionName}>{row.rank}</div>
                  <div className={styles.positionMoney}>{row.since}</div>
                  <div className={styles.positionMoney}>{row.salary !== 0 ? formatMoney(row.salary) : "-"}</div>
                  <div className={styles.positionMoney}>{row.bonus !== 0 ? formatMoney(row.bonus) : "-"}</div>
                </div>
              ))}
            </div>
          </div>
        </Dialog>

        {canCompensation && (
          <CompensationDialog
            isOpen={panel === "compensation"}
            onClose={close}
            store={store.compensation}
            employerId={employerId}
            canEdit={canEditCompensation}
          />
        )}

        <Dialog
          icon="info-sign"
          title="Назначенные премии"
          canEscapeKeyClose={true}
          canOutsideClickClose={false}
          isOpen={panel === "premiums"}
          onClose={close}
          style={{ width: `600px` }}
          backdropClassName="standard"
        >
          <div className={Classes.DIALOG_BODY}>
            <OwnPremiumsWidget store={store.premiums} orderAccess={orderAccess} />
          </div>
        </Dialog>

        <Dialog
          icon="info-sign"
          title="Закрепленный инвентарь"
          canEscapeKeyClose={true}
          canOutsideClickClose={false}
          isOpen={panel === "materials"}
          onClose={close}
          style={{ width: "600px" }}
          backdropClassName="standard"
        >
          <div className={Classes.DIALOG_BODY}>
            <OwnMaterialsWidget store={materials.list} orderAccess={orderAccess} employerId={employerId} />
          </div>
        </Dialog>

        <Dialog
          icon="info-sign"
          title="Права доступа"
          canEscapeKeyClose={true}
          canOutsideClickClose={false}
          isOpen={panel === "access"}
          onClose={close}
          style={{ width: `1180px` }}
          backdropClassName="standard"
        >
          <div className={`${Classes.DIALOG_BODY} ${styles.userAccess}`}>
            <RoleDetails
              role={store.details.role}
              modules={store.accessModules}
              personal={true}
              storageKey="employee"
              admin={{
                value: store.details.godMode,
                canChange: !adminReason,
                reason: adminReason,
                onChange: (value: boolean) => store.details.setGodMode(value),
              }}
              onToggle={(_: string, functionality: string, state: boolean) =>
                store.details.setFunctionality(functionality, state)
              }
            />
          </div>
        </Dialog>
      </div>
    )
  }
)

// Объединённая хронология «должность + ранг»: одна строка на каждую смену должности
// или ранга. Для строки-ранга подставляется должность, действовавшая на ту дату, и наоборот.
const parseSince = (s: string) => moment(s, ["DD.MM.YYYY", "DD.MM.YYYY HH:mm:ss", moment.ISO_8601], true)

export function buildPositionRankHistory(positions: any, grades: any, portfolioName: (id: string | null) => string) {
  const posList: any[] = (positions ? Array.from(positions) : []).filter((p: any) => p && p.since)
  const gradeList: any[] = (grades ? Array.from(grades) : []).filter((g: any) => g && g.since)

  const resolveLatest = (list: any[], d: number) => {
    let best: any = null
    let bestD = -Infinity
    for (const item of list) {
      const id = parseSince(item.since).valueOf()
      if (id <= d && id >= bestD) {
        best = item
        bestD = id
      }
    }
    return best
  }

  const rankLabel = (g: any) =>
    g && g.gradeGuid && g.gradeGuid !== "null" && g.percent > 0 ? `${(g.gradeName || "").trim()} — ${g.percent}%` : "-"

  const events = [
    ...posList.map((p) => ({ d: parseSince(p.since).valueOf(), since: p.since, kind: "position", ref: p })),
    ...gradeList.map((g) => ({ d: parseSince(g.since).valueOf(), since: g.since, kind: "grade", ref: g })),
  ].sort((a, b) => b.d - a.d)

  return events.map((e) => {
    const pos = e.kind === "position" ? e.ref : resolveLatest(posList, e.d)
    const grade = e.kind === "grade" ? e.ref : resolveLatest(gradeList, e.d)
    // перевод в другую организацию - строка самой должности, не смены ранга
    const transfer = e.kind === "position" && pos?.organizationChanged
    return {
      name: pos ? pos.name : "",
      organization: pos ? portfolioName(pos.portfolioId) : "",
      organizationNote: transfer ? (pos.seniorityReset ? "перевод, стаж заново" : "перевод, стаж сохранён") : "",
      department: pos && pos.department ? pos.department.name : "",
      rank: rankLabel(grade),
      since: e.since,
      salary: pos ? pos.salary : 0,
      bonus: pos ? pos.bonus : 0,
    }
  })
}
